/**
 * TRELLIS2 Inference Plugin
 *
 * Image -> 3D mesh (.glb) with PBR materials via TRELLIS.2.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { INFERENCE_API_URL, getImageBlob, imageBlobExtension, createObjectFromInference, inferencePost, type InferenceStatusCallback } from './shared';
import { declareInferencePlugin } from '../plugins/inference-adapter';

async function run(sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback): Promise<SceneObject> {
    const imageBlob = await getImageBlob(sourceObject, traceScene);
    const imgExt = imageBlobExtension(imageBlob);

    const response = await inferencePost(
        'trellis2',
        () => {
            const formData = new FormData();
            formData.append('image', imageBlob, `image.${imgExt}`);
            formData.append('format', 'glb');
            return [`${INFERENCE_API_URL}/trellis2/generate`, { method: 'POST', body: formData }];
        },
        undefined,
        onStatus,
    );

    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`TRELLIS2 error (${response.status}): ${errText}`);
    }

    const glbBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${sourceObject.name}.glb`,
        glbBlob,
    );

    return createObjectFromInference(traceScene, sourceObject, assetPath, '3d');
}

declareInferencePlugin({
    id: 'trellis2',
    sourceType: 'image',
    menuLabel: 'Make 3D (TRELLIS2)',
    menuOrder: 204,

    toolName: 'generate_3d',

    run,

    slashCommand: {
        name: 'trellis',
        description: 'Convert image to 3D mesh with PBR materials (TRELLIS2)',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
